/**
 * Human Resources (WALLIYO-0131).
 *
 * Four tabs, and the gate is one of them. Directory and Learning are open to
 * anybody who can sign in; Recruiting and Employees need
 * MANAGE_HUMAN_RESOURCES, and the seven sub-tabs hang under Employees so there
 * is one tab to close rather than seven to remember.
 *
 * A locked tab is drawn rather than hidden, so the shape of the screen is the
 * same for everybody, and its content is never read: the request is not made,
 * so there is nothing to leak even if the markup were inspected. The server
 * checks the same feature on every endpoint underneath.
 */

import type { StaffFeatureId } from "@wallyo/contracts/actions";
import type { StaffMemberListItem } from "@wallyo/contracts/console";
import type {
  Course,
  JobCandidate,
  JobOpening,
  PayrollLine,
  PerformanceReview,
  TimeOffRequest,
} from "@wallyo/contracts/console/hr";
import type { TeamListItem } from "@wallyo/contracts/console/configuration";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
  getDirectoryPerson,
  getLeaving,
  listDirectory,
  listRoles,
  listStaff,
  getPayroll,
  getReviewCycle,
  listAccessReviews,
  listCourses,
  listJobOpenings,
  listTeams,
  enrolInCourse,
  listPermissionHolders,
  listTimeOff,
} from "../../api/console-api";
import { useApi } from "../../api/context";
import { useAlerts } from "../../shell/alerts";
import { t, type MessageId } from "../../i18n";
import { useHash } from "../../navigation/use-hash";
import { PageTop } from "../../shell/PageTop";
import { useStaff } from "../../shell/staff-context";
import { DeactivateDialog, InviteDialog } from "../team/TeamDialogs";
import { RecordsTab } from "../team/TeamScreen";
import { DirectoryList, DirectoryPersonView, type DirectoryState, type PersonState } from "./DirectoryView";
import { JoiningView, type JoiningState } from "./JoiningView";
import { LeavingView, type LeavingState } from "./LeavingView";
import { AskTimeOffDialog, DecideTimeOffDialog } from "./TimeOffDialogs";
import { TimeOffView, type TimeOffState } from "./TimeOffView";
import { ReviewsView, type ReviewsState } from "./ReviewsView";
import { PayrollView, type PayrollState } from "./PayrollView";
import { ClosePayrollDialog, HoldLineDialog, OpenPayrollDialog, thisMonth } from "./PayrollDialogs";
import { PerformanceView, type PerformanceState } from "./PerformanceView";
import { OpenCycleDialog, WriteReviewDialog } from "./PerformanceDialogs";
import { RecruitingView, type RecruitingState } from "./RecruitingView";
import { LearningView, type LearningState } from "./LearningView";
import { CandidateDialog, CourseDialog, JobDialog, MoveCandidateDialog } from "./RecruitingDialogs";
import { WhoCanDoThisView } from "../team/WhoCanDoThisView";
import type { WhoState } from "../team/who-can-do-this";
import { DEFAULT_WHO_ACTION } from "../team/team-route";
import {
  canOpenHr,
  hrHref,
  HR_DIRECTORY_LINKS,
  parseHrHash,
  tabNeedsFeature,
  HR_SUB_TABS,
  HR_TABS,
  type DirectoryLinks,
  type HrSubTab,
  type HrTab,
} from "./hr-route";

const TAB_LABEL: Record<HrTab, MessageId> = {
  directory: "hr.tab.directory",
  recruiting: "hr.tab.recruiting",
  employees: "hr.tab.employees",
  learning: "hr.tab.learning",
};

const SUB_LABEL: Record<HrSubTab, MessageId> = {
  records: "hr.sub.records",
  joining: "hr.sub.joining",
  "time-off": "hr.sub.timeOff",
  performance: "hr.sub.performance",
  payroll: "hr.sub.payroll",
  reviews: "hr.sub.reviews",
  leaving: "hr.sub.leaving",
};

export function HrScreen({ tabsInSidebar }: { tabsInSidebar?: boolean }) {
  const { me } = useStaff();
  const features = me.features as readonly StaffFeatureId[];
  const hash = useHash();
  const route = parseHrHash(hash, features);
  const inHr = canOpenHr(features);

  /**
   * The title is the tab's, when the sidebar brought you straight to one.
   *
   * "Human Resources › Employees" with the sidebar already saying Employees is
   * the heading repeating the row you pressed.
   */
  /** True when the sidebar's row pointed at a sub-tab rather than the tab. */
  const onSubTabRow = Boolean(tabsInSidebar) && hash.split("/").length > 2;

  const TITLE_OF: Record<HrTab, MessageId> = {
    directory: "screen.as.directory",
    recruiting: "screen.as.recruiting",
    employees: "screen.as.employees",
    learning: "screen.as.learning",
  };

  return (
    <>
      <PageTop
        title={
          tabsInSidebar
            ? t(route.tab === "employees" && onSubTabRow ? SUB_LABEL[route.subTab] : TITLE_OF[route.tab])
            : t("screen.hr")
        }
      />
      {/* Not when the sidebar already lists these as rows: that is the same
          navigation, and drawing both puts it on the page twice. */}
      {tabsInSidebar ? null : (
        <div className="tabs payment-tabs" role="tablist" aria-label={t("hr.tabs.aria")}>
          {HR_TABS.map((tab) => (
            <TabLink key={tab} tab={tab} current={route.tab} locked={tabNeedsFeature(tab) && !inHr} />
          ))}
        </div>
      )}
      {/* The sub-strip belongs to the Employees row, which is the only way to
          reach Records, Joining, Performance, Reviews and Leaving. A row that
          points straight at a sub-tab — Payroll — is a destination of its own, so
          it draws no strip at all: the sidebar is the navigation (WALLIYO-0134). */}
      {route.tab === "employees" && !onSubTabRow ? (
        <div className="tabs sub-tabs" role="tablist" aria-label={t("hr.subTabs.aria")}>
          {HR_SUB_TABS.map((subTab) => (
            <SubTabLink key={subTab} subTab={subTab} current={route.subTab} />
          ))}
        </div>
      ) : null}
      <HrTabBody route={route} />
    </>
  );
}

function TabLink({ tab, current, locked }: { tab: HrTab; current: HrTab; locked: boolean }) {
  const on = tab === current;
  if (locked) {
    return (
      <span className="tab shut" role="tab" aria-selected={false} aria-disabled="true" title={t("hr.locked")}>
        {t(TAB_LABEL[tab])} <span aria-hidden="true">🔒</span>
      </span>
    );
  }
  return (
    <a className={on ? "tab on" : "tab"} href={hrHref({ tab })} role="tab" aria-selected={on}>
      {t(TAB_LABEL[tab])}
    </a>
  );
}

function SubTabLink({ subTab, current }: { subTab: HrSubTab; current: HrSubTab }) {
  const on = subTab === current;
  return (
    <a className={on ? "tab on" : "tab"} href={hrHref({ tab: "employees", subTab })} role="tab" aria-selected={on}>
      {t(SUB_LABEL[subTab])}
    </a>
  );
}

function HrTabBody({ route }: { route: ReturnType<typeof parseHrHash> }) {
  if (route.tab === "directory") {
    return route.uuid === null ? <DirectoryTab query={route.query} /> : <PersonTab uuid={route.uuid} />;
  }
  // Records is the staff list, the roles grid and "who can do this" — the
  // screen that used to be called Rôles & équipe, now where it belongs.
  if (route.tab === "employees" && route.subTab === "records") return <RecordsTab />;
  if (route.tab === "employees" && route.subTab === "joining") return <JoiningTab />;
  if (route.tab === "employees" && route.subTab === "leaving") return <LeavingTab who={route.who} />;
  if (route.tab === "employees" && route.subTab === "time-off") return <TimeOffTab />;
  if (route.tab === "employees" && route.subTab === "reviews") return <ReviewsTab />;
  if (route.tab === "employees" && route.subTab === "payroll") return <PayrollTab />;
  if (route.tab === "employees" && route.subTab === "performance") return <PerformanceTab />;
  if (route.tab === "recruiting") return <RecruitingTab />;
  return <LearningTab />;
}

export function DirectoryTab({ query, links = HR_DIRECTORY_LINKS }: { query: string; links?: DirectoryLinks }) {
  const { client } = useApi();
  const [state, setState] = useState<DirectoryState>({ status: "loading", people: null, total: 0 });
  const [reads, read] = useReducer((count: number) => count + 1, 0);
  const generation = useRef(0);

  useEffect(() => {
    const mine = ++generation.current;
    setState((was) => ({ status: "loading", people: was.people, total: was.total }));
    listDirectory(client, query).then(
      (data) => {
        if (generation.current !== mine) return;
        setState({ status: "ready", people: data.people, total: data.total });
      },
      (error: unknown) => {
        if (generation.current !== mine) return;
        setState((was) => ({ status: "failed", people: was.people, total: was.total, error }));
      },
    );
  }, [client, query, reads]);

  return (
    <DirectoryList
      state={state}
      query={query}
      links={links}
      // Searching must stay on the screen the search was typed into.
      onQuery={(next) => {
        window.location.hash = links.list(next);
      }}
      onRetry={read}
    />
  );
}

export function PersonTab({ uuid, links = HR_DIRECTORY_LINKS }: { uuid: number; links?: DirectoryLinks }) {
  const { client } = useApi();
  const [state, setState] = useState<PersonState>({ status: "loading", data: null });
  const [reads, read] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    let current = true;
    setState({ status: "loading", data: null });
    getDirectoryPerson(client, uuid).then(
      (data) => current && setState({ status: "ready", data }),
      (error: unknown) => current && setState({ status: "failed", data: null, error }),
    );
    return () => {
      current = false;
    };
  }, [client, uuid, reads]);

  return <DirectoryPersonView state={state} onRetry={read} links={links} />;
}

/**
 * Joining reads the same list Records reads: an invitation is a staff member
 * with `status: "invited"`, not a second kind of record. Inviting somebody is
 * the dialog that already exists, because there is one way to invite somebody
 * and it should not become two.
 */
function JoiningTab() {
  const { client } = useApi();
  const alerts = useAlerts();
  const [state, setState] = useState<JoiningState>({ status: "loading", staff: null, roles: [] });
  const [reads, read] = useReducer((count: number) => count + 1, 0);
  const [inviting, setInviting] = useState(false);
  const generation = useRef(0);

  // Read once per load rather than per render: two renders of the same list
  // must not disagree about where "recently" ends.
  const now = useMemo(() => new Date(), [reads]);

  useEffect(() => {
    const mine = ++generation.current;
    setState((was) => ({ status: "loading", staff: was.staff, roles: was.roles }));
    Promise.all([listStaff(client), listRoles(client)]).then(
      ([staff, roles]) => {
        if (generation.current !== mine) return;
        setState({ status: "ready", staff: staff.staffMembers, roles: roles.roles });
      },
      (error: unknown) => {
        if (generation.current !== mine) return;
        setState((was) => ({ status: "failed", staff: was.staff, roles: was.roles, error }));
      },
    );
  }, [client, reads]);

  return (
    <>
      <JoiningView state={state} now={now} onInvite={() => setInviting(true)} onRetry={read} />
      {inviting ? (
        <InviteDialog
          roles={state.roles}
          onClose={() => setInviting(false)}
          onReload={() => {
            setInviting(false);
            read();
          }}
          onDone={(message) => {
            alerts.ok(message);
            setInviting(false);
            read();
          }}
          // The list is read again after every invitation, so the row that
          // appears is the server's and not one guessed here.
          onMember={() => undefined}
        />
      ) : null}
    </>
  );
}

/**
 * Leaving reads the loose ends for one person, and hands each one to the
 * screen that fixes it. Deactivating is the dialog that already exists: there
 * is one way to deactivate somebody, and it should not become two.
 */
function LeavingTab({ who }: { who: string | null }) {
  const { client } = useApi();
  const alerts = useAlerts();
  const [people, setPeople] = useState<readonly StaffMemberListItem[] | null>(null);
  const [state, setState] = useState<LeavingState>({ status: "idle" });
  const [reads, read] = useReducer((count: number) => count + 1, 0);
  const [leaving, setLeaving] = useState<StaffMemberListItem | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    let current = true;
    listStaff(client).then(
      (data) => current && setPeople(data.staffMembers),
      () => current && setPeople([]),
    );
    return () => {
      current = false;
    };
  }, [client, reads]);

  useEffect(() => {
    if (who === null) {
      setState({ status: "idle" });
      return;
    }
    const mine = ++generation.current;
    setState({ status: "loading" });
    getLeaving(client, who).then(
      (data) => generation.current === mine && setState({ status: "ready", data }),
      (error: unknown) => generation.current === mine && setState({ status: "failed", error }),
    );
  }, [client, who, reads]);

  return (
    <>
      <LeavingView
        people={people}
        who={who}
        state={state}
        onWho={(next) => {
          window.location.hash = hrHref({ tab: "employees", subTab: "leaving", who: next });
        }}
        onRetry={read}
        onDeactivate={setLeaving}
      />
      {leaving ? (
        <DeactivateDialog
          member={leaving}
          onClose={() => setLeaving(null)}
          onReload={() => {
            setLeaving(null);
            read();
          }}
          onDone={(message) => {
            alerts.ok(message);
            setLeaving(null);
            read();
          }}
          // Read again afterwards, so what shows is the server's answer and
          // not one guessed here.
          onMember={() => undefined}
        />
      ) : null}
    </>
  );
}

/**
 * Time off. Everything on it is open to everybody: asking is your own, and
 * deciding is whoever leads your team. The whole-company list only comes back
 * for somebody in HR, and never with a decide button — seeing everybody's
 * leave is not approving everybody's leave.
 */
function TimeOffTab() {
  const { client } = useApi();
  const alerts = useAlerts();
  const [state, setState] = useState<TimeOffState>({ status: "loading", data: null });
  const [reads, read] = useReducer((count: number) => count + 1, 0);
  const [asking, setAsking] = useState(false);
  const [deciding, setDeciding] = useState<{ request: TimeOffRequest; approve: boolean } | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    const mine = ++generation.current;
    setState((was) => ({ status: "loading", data: was.data }));
    listTimeOff(client).then(
      (data) => generation.current === mine && setState({ status: "ready", data }),
      (error: unknown) =>
        generation.current === mine && setState((was) => ({ status: "failed", data: was.data, error })),
    );
  }, [client, reads]);

  const done = (message: string) => {
    alerts.ok(message);
    setAsking(false);
    setDeciding(null);
    read();
  };

  return (
    <>
      <TimeOffView
        state={state}
        onAsk={() => setAsking(true)}
        onDecide={(request, approve) => setDeciding({ request, approve })}
        onRetry={read}
      />
      {asking ? (
        <AskTimeOffDialog
          onClose={() => setAsking(false)}
          onReload={() => {
            setAsking(false);
            read();
          }}
          onDone={done}
        />
      ) : null}
      {deciding ? (
        <DecideTimeOffDialog
          request={deciding.request}
          approve={deciding.approve}
          onClose={() => setDeciding(null)}
          onReload={() => {
            setDeciding(null);
            read();
          }}
          onDone={done}
        />
      ) : null}
    </>
  );
}

/**
 * Reviews. The two lists are one read; "who can do this" is its own, because
 * it is a different question about a different thing and the answer changes
 * when the action does.
 */
function ReviewsTab() {
  const { client } = useApi();
  const [state, setState] = useState<ReviewsState>({ status: "loading", data: null });
  const [reads, read] = useReducer((count: number) => count + 1, 0);
  const generation = useRef(0);

  const [actionId, setActionId] = useState<string>(DEFAULT_WHO_ACTION);
  const [who, setWho] = useState<WhoState>({ status: "loading", data: null });
  const [whoReads, readWho] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    const mine = ++generation.current;
    setState((was) => ({ status: "loading", data: was.data }));
    listAccessReviews(client).then(
      (data) => generation.current === mine && setState({ status: "ready", data }),
      (error: unknown) =>
        generation.current === mine && setState((was) => ({ status: "failed", data: was.data, error })),
    );
  }, [client, reads]);

  useEffect(() => {
    let current = true;
    setWho((was) => ({ status: "loading", data: was.data }));
    listPermissionHolders(client, actionId).then(
      (data) => current && setWho({ status: "ready", data }),
      (error: unknown) => current && setWho((was) => ({ status: "failed", data: was.data, error })),
    );
    return () => {
      current = false;
    };
  }, [client, actionId, whoReads]);

  return (
    <ReviewsView
      state={state}
      onRetry={read}
      who={<WhoCanDoThisView actionId={actionId} state={who} onAction={setActionId} onRetry={readWho} />}
    />
  );
}

/**
 * Payroll. The month lives in this component rather than the hash: it is a
 * thing being worked on, not a place, and a link to "September" a year from
 * now would open a month the reader did not mean.
 */
function PayrollTab() {
  const { client } = useApi();
  const alerts = useAlerts();
  const [month, setMonth] = useState("");
  const [state, setState] = useState<PayrollState>({ status: "loading", data: null });
  const [reads, read] = useReducer((count: number) => count + 1, 0);
  const [opening, setOpening] = useState(false);
  const [holding, setHolding] = useState<{ line: PayrollLine; hold: boolean } | null>(null);
  const [closing, setClosing] = useState(false);
  const generation = useRef(0);

  useEffect(() => {
    const mine = ++generation.current;
    setState((was) => ({ status: "loading", data: was.data }));
    getPayroll(client, month).then(
      (data) => {
        if (generation.current !== mine) return;
        setState({ status: "ready", data });
        // The server decides which month is shown when none was asked for, so
        // the picker follows its answer rather than guessing alongside it.
        if (month === "" && data.run) setMonth(data.run.periodMonth.slice(0, 7));
      },
      (error: unknown) =>
        generation.current === mine && setState((was) => ({ status: "failed", data: was.data, error })),
    );
  }, [client, month, reads]);

  const done = (message: string) => {
    alerts.ok(message);
    setOpening(false);
    setHolding(null);
    setClosing(false);
    read();
  };

  const run = state.data?.run ?? null;

  return (
    <>
      <PayrollView
        state={state}
        month={month === "" ? thisMonth() : month}
        onMonth={setMonth}
        onOpen={() => setOpening(true)}
        onHold={(line, hold) => setHolding({ line, hold })}
        onClose={() => setClosing(true)}
        onRetry={read}
      />
      {opening ? (
        <OpenPayrollDialog
          onClose={() => setOpening(false)}
          onReload={() => {
            setOpening(false);
            read();
          }}
          onDone={done}
        />
      ) : null}
      {holding ? (
        <HoldLineDialog
          line={holding.line}
          hold={holding.hold}
          onClose={() => setHolding(null)}
          onReload={() => {
            setHolding(null);
            read();
          }}
          onDone={done}
        />
      ) : null}
      {closing && run ? (
        <ClosePayrollDialog
          payrollRunId={run.payrollRunId}
          version={run.version}
          onClose={() => setClosing(false)}
          onReload={() => {
            setClosing(false);
            read();
          }}
          onDone={done}
        />
      ) : null}
    </>
  );
}

/**
 * Performance. The cycle lives in this component rather than the hash, like
 * the payroll month: it is a thing being worked on, not a place.
 */
function PerformanceTab() {
  const { client } = useApi();
  const alerts = useAlerts();
  const [cycleId, setCycleId] = useState("");
  const [state, setState] = useState<PerformanceState>({ status: "loading", data: null });
  const [reads, read] = useReducer((count: number) => count + 1, 0);
  const [opening, setOpening] = useState(false);
  const [writing, setWriting] = useState<PerformanceReview | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    const mine = ++generation.current;
    setState((was) => ({ status: "loading", data: was.data }));
    getReviewCycle(client, cycleId).then(
      (data) => {
        if (generation.current !== mine) return;
        setState({ status: "ready", data });
        // The server decides which cycle is shown when none was asked for.
        if (cycleId === "" && data.cycle) setCycleId(data.cycle.reviewCycleId);
      },
      (error: unknown) =>
        generation.current === mine && setState((was) => ({ status: "failed", data: was.data, error })),
    );
  }, [client, cycleId, reads]);

  const done = (message: string) => {
    alerts.ok(message);
    setOpening(false);
    setWriting(null);
    read();
  };

  return (
    <>
      <PerformanceView
        state={state}
        onCycle={setCycleId}
        onOpen={() => setOpening(true)}
        onWrite={setWriting}
        onRetry={read}
      />
      {opening ? (
        <OpenCycleDialog
          onClose={() => setOpening(false)}
          onReload={() => {
            setOpening(false);
            read();
          }}
          onDone={done}
        />
      ) : null}
      {writing ? (
        <WriteReviewDialog
          review={writing}
          onClose={() => setWriting(null)}
          onReload={() => {
            setWriting(null);
            read();
          }}
          onDone={done}
        />
      ) : null}
    </>
  );
}

/**
 * Recruiting. The teams list comes with it because a vacancy belongs to the
 * team that owns the work, and the form needs the names to offer.
 */
function RecruitingTab() {
  const { client } = useApi();
  const alerts = useAlerts();
  const [state, setState] = useState<RecruitingState>({ status: "loading", data: null });
  const [teams, setTeams] = useState<readonly TeamListItem[]>([]);
  const [reads, read] = useReducer((count: number) => count + 1, 0);
  const [job, setJob] = useState<{ opening: JobOpening | null } | null>(null);
  const [adding, setAdding] = useState<JobOpening | null>(null);
  const [moving, setMoving] = useState<JobCandidate | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    const mine = ++generation.current;
    setState((was) => ({ status: "loading", data: was.data }));
    Promise.all([listJobOpenings(client), listTeams(client)]).then(
      ([data, teamList]) => {
        if (generation.current !== mine) return;
        setState({ status: "ready", data });
        setTeams(teamList.teams);
      },
      (error: unknown) =>
        generation.current === mine && setState((was) => ({ status: "failed", data: was.data, error })),
    );
  }, [client, reads]);

  const done = (message: string) => {
    alerts.ok(message);
    setJob(null);
    setAdding(null);
    setMoving(null);
    read();
  };
  const close = () => {
    setJob(null);
    setAdding(null);
    setMoving(null);
  };

  return (
    <>
      <RecruitingView
        state={state}
        onOpenJob={() => setJob({ opening: null })}
        onEditJob={(opening) => setJob({ opening })}
        onAddCandidate={setAdding}
        onMove={setMoving}
        onRetry={read}
      />
      {job ? (
        <JobDialog
          opening={job.opening}
          teams={teams}
          onClose={close}
          onReload={() => {
            close();
            read();
          }}
          onDone={done}
        />
      ) : null}
      {adding ? (
        <CandidateDialog
          opening={adding}
          onClose={close}
          onReload={() => {
            close();
            read();
          }}
          onDone={done}
        />
      ) : null}
      {moving ? (
        <MoveCandidateDialog
          candidate={moving}
          onClose={close}
          onReload={() => {
            close();
            read();
          }}
          onDone={done}
        />
      ) : null}
    </>
  );
}

/**
 * Learning. Enrolling is a write with no dialog: it needs nobody's permission
 * and says nothing a reason would add, so asking for one would be asking for
 * the sake of it.
 */
function LearningTab() {
  const { client } = useApi();
  const alerts = useAlerts();
  const [state, setState] = useState<LearningState>({ status: "loading", data: null });
  const [reads, read] = useReducer((count: number) => count + 1, 0);
  const [adding, setAdding] = useState(false);
  const generation = useRef(0);

  useEffect(() => {
    const mine = ++generation.current;
    setState((was) => ({ status: "loading", data: was.data }));
    listCourses(client).then(
      (data) => generation.current === mine && setState({ status: "ready", data }),
      (error: unknown) =>
        generation.current === mine && setState((was) => ({ status: "failed", data: was.data, error })),
    );
  }, [client, reads]);

  const enrol = (course: Course, progress: number) => {
    void enrolInCourse(client, course.courseId, { progress }).then(
      ({ enrolment }) => {
        alerts.ok(
          enrolment.status === "done"
            ? t("learning.finished", { title: course.title })
            : t("learning.enrolledDone", { title: course.title }),
        );
        read();
      },
      (error: unknown) => {
        alerts.problem(error);
        read();
      },
    );
  };

  return (
    <>
      <LearningView state={state} onAddCourse={() => setAdding(true)} onEnrol={enrol} onRetry={read} />
      {adding ? (
        <CourseDialog
          onClose={() => setAdding(false)}
          onReload={() => {
            setAdding(false);
            read();
          }}
          onDone={(message) => {
            alerts.ok(message);
            setAdding(false);
            read();
          }}
        />
      ) : null}
    </>
  );
}
